import type { VarlessCopyId } from '@npc/copy'

import { T } from '../../copy/text'
import { humanBytes } from './attachments'
import type { LuluMessage } from './queries'

/**
 * 已经发出去的那些附件，画在它们那条消息底下。
 *
 * # 在这之前，发出去就再也看不见了
 *
 * 服务端这一半早就齐了：每条消息的 DTO **恒带** `attachments`
 * （`routes.go` 的 `attachmentDTO`，缺席与空数组只留一档），
 * 每个附件带一条 `download_url`。而前端一行都没读过它——
 * `grep -rn download_url apps/ packages/` 只命中生成的类型，零消费方。
 *
 * 于是用户看到的是：选文件的时候有一排、传的时候有进度、发出去的那一刻
 * **整排消失**，此后这条消息看起来跟没带文件一样。他上传的那个东西
 * 去哪了、还在不在、能不能拿回来，屏幕上一个字都没有。
 *
 * # 与 `attachment-tray.tsx` 是两件事，不是一个组件的两种状态
 *
 * 那一排是**还没发出去的草稿**（`DraftAttachment`：四态、进度、能「不要了」）。
 * 这一排是**消息的一部分**（`ConversationAttachment`：只有一态，删不掉，
 * 多一条能取回来的路）。
 *
 * 判据是 CLAUDE.md §8 那条「两件事如果要求用户做的下一个动作不同，
 * 就不许合成一种说法」：那边的下一个动作是「等它传完 / 重试 / 摘掉」，
 * 这边的下一个动作是「点开看看」。合成一个组件就得在里面写一串
 * 「发出去了没有」的分支，而分支写错的表现是**在已发送的消息上出现一颗
 * 「不要了」，点了服务端回 404**。
 */

/**
 * 一个已发送附件的样子。**从生成的契约类型上派生，不另写一份**——
 * 手抄一遍的下场是服务端加一个字段而这边不知道。
 */
type MessageAttachment = NonNullable<LuluMessage['attachments']>[number]

/**
 * 「这是个什么」。
 *
 * # 为什么不直接把 MIME 印出来
 *
 * `image/png` 是第二层的东西（CLAUDE.md §3：术语藏在第二层）。
 * 第一屏要回答的是「哦，是张图」——而 `application/vnd.…` 那种串
 * 对着屏幕的人只有一个作用：让他觉得这一行不是给他看的。
 *
 * # 十种 MIME 收成六句
 *
 * 因为**用户分不出也不需要分** png 与 webp：他要判断的是
 * 「这个我点开能看吗」。四种图片说成同一句「图片」不是偷懒，
 * 是把区别留在了真正有区别的地方。
 *
 * # 这张表是编译期哨兵，不是一张要人记得维护的名单
 *
 * key 的类型就是服务端那个封闭枚举（`ConversationAttachment.mime`）。
 * 服务端哪天收一种新格式、重新生成 `packages/api-client` 的那一刻，
 * **这张表少一行就编不过**。CLAUDE.md §8「两边必须一起改的约定，
 * 写成编译期哨兵，不要写成注释」说的正是这个位置。
 */
const KIND_COPY: Record<MessageAttachment['mime'], VarlessCopyId> = {
  'image/png': 'lulu_workbench.attach.kind_image',
  'image/jpeg': 'lulu_workbench.attach.kind_image',
  'image/webp': 'lulu_workbench.attach.kind_image',
  'image/gif': 'lulu_workbench.attach.kind_image',
  'application/pdf': 'lulu_workbench.attach.kind_pdf',
  'text/plain': 'lulu_workbench.attach.kind_text',
  'text/markdown': 'lulu_workbench.attach.kind_text',
  'text/csv': 'lulu_workbench.attach.kind_table',
  'application/json': 'lulu_workbench.attach.kind_json',
  'application/zip': 'lulu_workbench.attach.kind_zip',
}

/**
 * 这条 `download_url` 能不能真的挂到 `href` 上。
 *
 * # 它不是在防服务端
 *
 * 服务端的契约是一条同源路径（`conversations.AttachmentURL` 返回
 * `/api/v1/conversations/{cid}/attachments/{aid}`），今天这一侧没有问题。
 *
 * 挡的是**「拿到什么就渲染什么」这个形状本身**：一个 `href` 直接吃
 * 远端给的字符串，那条路上任何一天出现 `javascript:` 或者别人的域名，
 * 用户点的就是它，而界面上没有任何异样。
 *
 * `//` 开头单独拒掉：那是协议相对 URL，`//example.com/x` 会跳到别的域，
 * 而它长得跟一条路径一模一样。
 *
 * **画不出链接，好过画一条把人送去别处的链接。** 空串也走这条：
 * 一个 `href=""` 的链接点下去是重新加载当前页——那比点不动更糟，
 * 他会以为自己把正在看的东西弄没了。
 */
export function downloadHref(url: string | undefined | null): string | null {
  if (typeof url !== 'string') return null
  if (!url.startsWith('/')) return null
  if (url.startsWith('//')) return null
  return url
}

export function MessageAttachments({ items }: { readonly items: readonly MessageAttachment[] }) {
  if (items.length === 0) return null
  return (
    <ul data-testid="lulu-message-attachments" className="mt-2 grid gap-1">
      {items.map((a) => {
        const href = downloadHref(a.download_url)
        const kindId = KIND_COPY[a.mime]
        // 一行里只有名字是长度不可控的，所以只有它 `min-w-0 flex-1 truncate`，
        // 其余一律 `shrink-0`。
        //
        // **这不是审美**：attachment-tray 在 390 那一档真踩过——一段长度由
        // 别人说了算的文字坐进同一个 flex 里，唯一能缩的名字被挤成 0px 宽，
        // 屏幕上是「文件名没了，一条东西横着捅出去 664px」。
        // 这里能横出去的是文件名，而截断它不丢信息（鼠标停上去有完整的）。
        const row = (
          <>
            <span
              data-testid="lulu-message-attachment-name"
              title={a.name}
              className="min-w-0 flex-1 truncate text-npc-caption text-npc-lulu-t2"
            >
              {a.name}
            </span>
            {/* 认不出的 MIME 就不说「这是个什么」。**不编一句**——
                编出来的那句会让他按一个错的预期去点。 */}
            {kindId === undefined ? null : (
              <span
                data-testid="lulu-message-attachment-kind"
                className="shrink-0 text-npc-meta text-npc-lulu-t4"
              >
                <T id={kindId} />
              </span>
            )}
            <span
              data-testid="lulu-message-attachment-size"
              className="shrink-0 text-npc-meta text-npc-lulu-t4"
            >
              {humanBytes(a.bytes)}
            </span>
            {/* 「下载」这两个字是这一行**唯一的可点提示**。
                一个只有描边的行看不出能不能点，而这一行存在的全部理由
                就是能把文件取回来。链接画不出来时它也不出现——
                一颗点了没反应的按钮比没有按钮更让人怀疑东西丢了。 */}
            {href === null ? null : (
              <span
                data-testid="lulu-message-attachment-download"
                className="shrink-0 text-npc-meta text-npc-lulu-t2"
              >
                <T id="lulu_workbench.attach.download" />
              </span>
            )}
          </>
        )
        const className =
          'flex items-center gap-2 rounded-npc-chip border border-npc-lulu-line-2 bg-npc-lulu-bg-1 px-2 py-1'
        return (
          /* `min-w-0` **不是可选的**（2026-09-02 在真浏览器 390 档量出来的）。
             grid item 的 `min-width` 默认是 `auto`，解析成它的 min-content——
             而这一行里那个 `white-space: nowrap` 的文件名让 min-content
             等于整串名字的宽度，于是这一列的轨道被撑到比容器还宽：

                 容器 clientWidth 358px  →  这一列 scrollWidth 640px

             撑破的是**整个 ul**，所以同一条消息里的每一行都跟着变宽——
             屏幕上是「三行附件一起横着捅出去，图片/大小/下载全被推到屏幕外」。

             名字那一格上的 `min-w-0` 拦不住它：当场量过，
             给 `<a>` 加 `min-width:0` 时 scrollWidth 还是 640。
             **要加在 grid item 这一层**，这一条就是这么定下来的。 */
          <li key={a.id} className="min-w-0">
            {href === null ? (
              <div data-testid="lulu-message-attachment" data-mime={a.mime} className={className}>
                {row}
              </div>
            ) : (
              /* `download` 之外不做任何花活：响应头那一半服务端已经定死了
                 （Content-Disposition: attachment 与 X-Content-Type-Options:
                 nosniff）。用 fetch 转 blob 再造一个 URL 的写法在这里只会
                 多出一份要自己回收的内存，以及一条绕开浏览器下载栏的路。 */
              <a
                href={href}
                download
                data-testid="lulu-message-attachment"
                data-mime={a.mime}
                className={className}
              >
                {row}
              </a>
            )}
          </li>
        )
      })}
    </ul>
  )
}
